import { CONVERSATIONS_API_URL, getConversationsAuthToken } from '@/config/env'

export interface BetterplaceListing {
  property_id?: string
  id?: string | number
  changed_at?: number
  title?: string
  price?: number
  sale_price?: number
  sale_price_per_sqm?: number
  total_area?: number
  rooms?: number
  bathrooms?: number
  floor_number?: string | number
  thumbnails?: string[]
  images?: string[]
  ref?: string
  property_url?: string
  url?: string
  coordinates?: { latitude?: number; longitude?: number }
  location?: {
    address?: string
    city?: string
    province?: string
    postalCode?: string
    coordinates?: { latitude?: number; longitude?: number }
  }
  propertyDetails?: {
    bedrooms?: number
    bathrooms?: number
    surfaceAreaSqm?: number
  }
  externalId?: string
  source?: string | {
    sourceKey?: string
    [key: string]: unknown
  }
  description?: string
  contacts?: string[]
  property?: CombinedPropertyLeadsProperty
  [key: string]: unknown
}

export interface BetterplaceListingsResponse {
  success: boolean
  results: BetterplaceListing[]
  count: number
  page: number
  per_page: number
  error?: string
}

export interface BetterplaceSendTemplatePayload {
  property_id: string
  template_name: string
  language_code?: string
  phone_numbers?: string[]
  components?: Record<string, unknown>
}

export type BetterplaceTemplateVariableKey =
  | 'buyer_name'
  | 'buyer_phone'
  | 'property_label'
  | 'property_url'
  | 'property_price'
  | 'property_ref'
  | 'cross_id'

export interface BetterplaceSendTemplateResult {
  success: boolean
  property_id: string
  results: Array<{
    success: boolean
    message_id?: string
    wa_id?: string
    phone_number?: string
    execution_id?: string
    dedupe_key?: string
    skipped?: boolean
    status?: string
    error?: string
  }>
  failed: number
}

export interface BetterplaceSyncConfig {
  enabled: boolean
  interval_minutes: number
  max_listings: number
}

/** Cross-criteria from proxy (GET); omit expiresInDays when updating. */
export interface CrossCriteria {
  roomsVariance?: number
  bathroomsVariance?: number
  priceVarianceDown?: number
  priceVarianceUp?: number
  distanceKm?: number
  expiresInDays?: number
}

/** Cross-settings (templates + phone) managed by conversations; snake_case. */
export interface CrossSettings {
  template_buyers?: string
  template_buyers_language?: string
  template_buyers_variables?: BetterplaceTemplateVariableKey[]
  template_coordination?: string
  template_coordination_language?: string
  template_coordination_variables?: BetterplaceTemplateVariableKey[]
  coordination_phone?: string
}

export interface CombinedPropertyLeadsProperty {
  id?: string | number
  ref?: string
  title: string
  price: number
  location?: {
    address?: string
    city?: string
    province?: string
    postalCode?: string
    coordinates?: { latitude?: number; longitude?: number }
  }
  propertyDetails?: {
    bedrooms?: number
    bathrooms?: number
    surfaceAreaSqm?: number
  }
  propertyType?: string
  operation?: string
  description?: string
  images: string[]
  externalId?: string
  url: string
}

export interface CombinedPropertyLeads {
  crossId: string
  expired: boolean
  property: CombinedPropertyLeadsProperty
  interestedClients: Array<{
    name: string
    phone: string
  }>
  betterplaceSellerLead: {
    name: string
    phone: string
    url: string
  } | null
}

interface CombinedPropertyLeadsResponse {
  success: boolean
  data: CombinedPropertyLeads
}

class BetterplaceServiceClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = CONVERSATIONS_API_URL || ''
  }

  private getAuthToken(): string {
    return getConversationsAuthToken()
  }

  private async buildError(response: Response): Promise<Error> {
    const contentType = response.headers.get('content-type') || ''

    if (contentType.includes('application/json')) {
      try {
        const payload = await response.json() as Record<string, unknown>
        const message =
          (typeof payload.error === 'string' && payload.error) ||
          (typeof payload.message === 'string' && payload.message) ||
          (typeof payload.detail === 'string' && payload.detail) ||
          response.statusText

        return new Error(`HTTP ${response.status}: ${message}`)
      } catch {
        return new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
    }

    const errorText = await response.text()
    return new Error(`HTTP ${response.status}: ${errorText || response.statusText}`)
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options
    }

    const token = this.getAuthToken()
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Token ${token}`,
      }
    }

    const response = await fetch(url, config)
    if (!response.ok) {
      throw await this.buildError(response)
    }

    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return response.json()
    }
    return response.text() as T
  }

  async listProperties(params?: { page?: number; per_page?: number }): Promise<BetterplaceListingsResponse> {
    const query = new URLSearchParams()
    if (params?.page) query.set('page', String(params.page))
    if (params?.per_page) query.set('per_page', String(params.per_page))
    const queryString = query.toString()
    const endpoint = `/api/v1/betterplace/properties/${queryString ? `?${queryString}` : ''}`
    return this.request<BetterplaceListingsResponse>(endpoint)
  }

  async sendTemplateToPropertyContacts(payload: BetterplaceSendTemplatePayload): Promise<BetterplaceSendTemplateResult> {
    return this.request<BetterplaceSendTemplateResult>('/api/v1/betterplace/properties/send-template/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async getSyncConfig(): Promise<BetterplaceSyncConfig> {
    return this.request<BetterplaceSyncConfig>('/api/v1/betterplace/sync-config/')
  }

  async updateSyncConfig(payload: Partial<BetterplaceSyncConfig>): Promise<BetterplaceSyncConfig & { success: boolean }> {
    return this.request<BetterplaceSyncConfig & { success: boolean }>('/api/v1/betterplace/sync-config/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async getCrossCriteria(): Promise<CrossCriteria> {
    const res = await this.request<{ success: boolean; data: CrossCriteria }>('/api/v1/betterplace/cross-criteria/')
    if (!res.success || res.data == null) return {}
    return res.data
  }

  async updateCrossCriteria(data: Omit<CrossCriteria, 'expiresInDays'>): Promise<CrossCriteria> {
    const res = await this.request<{ success: boolean; data: CrossCriteria }>('/api/v1/betterplace/cross-criteria/', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    if (!res.success || res.data == null) return {}
    return res.data
  }

  async getTemplateDispatch(): Promise<{ enabled: boolean }> {
    const res = await this.request<{ success: boolean; enabled: boolean }>('/api/v1/betterplace/template-dispatch/')
    return { enabled: Boolean(res.enabled) }
  }

  async updateTemplateDispatch(enabled: boolean): Promise<{ enabled: boolean }> {
    const res = await this.request<{ success: boolean; enabled: boolean }>('/api/v1/betterplace/template-dispatch/', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    })
    return { enabled: Boolean(res.enabled) }
  }

  async getCrossSettings(): Promise<CrossSettings> {
    const res = await this.request<{ success: boolean; data: CrossSettings }>('/api/v1/betterplace/cross-settings/')
    if (!res.success || res.data == null) return {}
    return res.data
  }

  async updateCrossSettings(data: Partial<CrossSettings>): Promise<CrossSettings> {
    const res = await this.request<{ success: boolean; data: CrossSettings }>('/api/v1/betterplace/cross-settings/', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    if (!res.success || res.data == null) return {}
    return res.data
  }

  async getCombinedPropertyLeads(id: string): Promise<CombinedPropertyLeads> {
    const response = await this.request<CombinedPropertyLeadsResponse | CombinedPropertyLeads>(
      `/api/v1/betterplace/combined-property-leads/${id}/`
    )
    if ('data' in response) {
      return response.data
    }
    return response
  }
}

export const betterplaceService = new BetterplaceServiceClient()
